import type { DocsLink } from '../../types';
import { docsUrlWarning, isRenderableDocsUrl } from '../../utils/docsLinks';

interface DocsLinksEditorProps {
  /**
   * The working rows. Held raw — including half-typed and blank ones — so the
   * admin can add a row and fill it in without it vanishing mid-keystroke.
   * `cleanDocsLinks` is applied at SAVE time by the owning editor, not here.
   */
  value: DocsLink[];
  onChange: (next: DocsLink[]) => void;
  disabled?: boolean;
  /** What these docs describe, e.g. "brand" or "manufacturer". */
  entityNoun?: string;
}

/**
 * Repeatable `{title, url}` row editor for an entity's {@link DocsLink} array —
 * the write half of {@link import('./DocsLinkChip').DocsLinkChip}.
 *
 * Validation is advisory throughout (see `docsUrlWarning`): a row with a URL we
 * can't linkify still saves, and warns that it will render as plain text. The
 * convention matches `yearCeased` — this form must never trap an admin who is
 * mid-paste.
 */
export function DocsLinksEditor({
  value,
  onChange,
  disabled = false,
  entityNoun = 'entity',
}: DocsLinksEditorProps) {
  const update = (index: number, patch: Partial<DocsLink>) =>
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const remove = (index: number) =>
    onChange(value.filter((_, i) => i !== index));

  const add = () => onChange([...value, { title: '', url: '' }]);

  return (
    <div>
      <label className='block text-sm font-medium text-gray-700 mb-1'>
        Documentation links
      </label>
      <p className='text-xs text-gray-500 mb-2'>
        Confluence pages for this {entityNoun} — classification reports, suffix
        conventions, open questions. Surfaced as a “Docs” chip in the calibre
        editor, the reclassify wizard and the bulk-import calibre step, so a
        curator meets them at the point of the decision.
      </p>

      {value.length === 0 && (
        <p className='text-xs text-gray-400 italic mb-2'>No documentation links.</p>
      )}

      <div className='flex flex-col gap-2'>
        {value.map((row, index) => {
          const warning = docsUrlWarning(row.url);
          return (
            <div
              key={index}
              className='border border-gray-200 rounded-lg p-3 bg-gray-50/50'
            >
              <div className='flex items-start gap-2'>
                <div className='flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-5 gap-2'>
                  <div className='sm:col-span-2'>
                    <label className='block text-xs font-medium text-gray-600 mb-1'>
                      Title
                    </label>
                    <input
                      type='text'
                      value={row.title}
                      disabled={disabled}
                      placeholder='e.g., Calibre classification'
                      aria-label={`Documentation link ${index + 1} title`}
                      onChange={(e) => update(index, { title: e.target.value })}
                      className='w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:opacity-50'
                    />
                  </div>
                  <div className='sm:col-span-3'>
                    <label className='block text-xs font-medium text-gray-600 mb-1'>
                      URL
                    </label>
                    <input
                      type='url'
                      value={row.url}
                      disabled={disabled}
                      placeholder='https://...'
                      aria-label={`Documentation link ${index + 1} URL`}
                      onChange={(e) => update(index, { url: e.target.value })}
                      className='w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:opacity-50'
                    />
                  </div>
                </div>
                <button
                  type='button'
                  onClick={() => remove(index)}
                  disabled={disabled}
                  aria-label={`Remove documentation link ${index + 1}`}
                  className='mt-5 px-2 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded disabled:opacity-50'
                >
                  Remove
                </button>
              </div>
              {warning && (
                <p className='mt-1 text-xs text-amber-600'>{warning}</p>
              )}
              {isRenderableDocsUrl(row.url) && (
                <a
                  href={row.url.trim()}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='mt-1 inline-block text-xs text-blue-600 hover:underline'
                >
                  Open in a new tab ↗
                </a>
              )}
            </div>
          );
        })}
      </div>

      <button
        type='button'
        onClick={add}
        disabled={disabled}
        className='mt-3 text-sm px-3 py-1.5 rounded-lg border border-dashed border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50'
      >
        + Add link
      </button>
    </div>
  );
}
